// Client layer (TS-2.5.03) - BRD section 8.6, UC-15/UC-16. Directory search (access.ts's
// searchDirectory, for a person the portfolio has never assigned a role to) sits beside the
// existing-user list (listUsers) since both feed the same assignRole call - the API makes no
// create/update distinction, an assignment upserts.
import { useCallback, useEffect, useState } from "react";
import { ApiError } from "../../api/ApiError";
import type { DirectoryPerson, UserProfile } from "../../api/generated/contracts";
import { PortfolioRoles } from "../../api/generated/contracts";
import { accessApi } from "../../api/access";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { FormField } from "../../components/FormField";
import { formFieldControlProps } from "../../components/formFieldControlProps";
import { strings } from "../../strings";

const ROLES = [PortfolioRoles.Viewer, PortfolioRoles.Contributor, PortfolioRoles.ProjectManager, PortfolioRoles.Admin];

const F = strings.admin.users;

// Humanized DISPLAY label for a raw PortfolioRoles value ("ProjectManager" -> "Project Manager").
// The value sent to the API is always the raw role string; a role this map does not know (a
// server-side addition) falls back to its raw spelling rather than disappearing.
function roleLabel(role: string): string {
  return (F.roleLabels as Record<string, string>)[role] ?? role;
}

export function UsersAdminPanel() {
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState<readonly UserProfile[]>([]);
  const [directoryResults, setDirectoryResults] = useState<readonly DirectoryPerson[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);

  const [pendingAssignment, setPendingAssignment] = useState<{ principalId: string; role: string; concurrencyToken: string | null } | null>(null);
  const [assigning, setAssigning] = useState(false);

  const refresh = useCallback(() => {
    setLoading(true);
    void accessApi
      .listUsers({ searchTerm: searchTerm.trim().length > 0 ? searchTerm.trim() : undefined })
      .then((page) => {
        setUsers(page.items);
        setError(null);
      })
      .catch((cause: unknown) => {
        setError(cause instanceof ApiError ? cause : new ApiError("network", 0, "The user list could not be loaded."));
      })
      .finally(() => setLoading(false));
  }, [searchTerm]);

  useEffect(refresh, [refresh]);

  useEffect(() => {
    if (searchTerm.trim().length === 0) {
      setDirectoryResults([]);
      return;
    }
    void accessApi
      .searchDirectory(searchTerm.trim())
      .then(setDirectoryResults)
      .catch(() => setDirectoryResults([]));
  }, [searchTerm]);

  async function confirmAssign() {
    if (!pendingAssignment) return;
    setAssigning(true);
    try {
      await accessApi.assignRole(pendingAssignment);
      setPendingAssignment(null);
      refresh();
    } finally {
      setAssigning(false);
    }
  }

  return (
    <section className="eg-admin-users">
      <h2>{F.heading}</h2>

      {/* FormField rather than a hand-rolled label pair, matching every other admin form (D-244). */}
      <div className="eg-admin__toolbar">
        <FormField id="eg-admin-user-search" label={F.search} description={F.searchHint}>
          <input
            type="search"
            placeholder={F.searchPlaceholder}
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            {...formFieldControlProps("eg-admin-user-search", F.searchHint)}
          />
        </FormField>
      </div>

      {loading && users.length === 0 && (
        <div role="status" aria-live="polite">
          {F.loading}
        </div>
      )}

      {error && (
        <div role="alert">
          <p>{F.errorTitle}</p>
          <p>{error.message}</p>
        </div>
      )}

      {!loading && users.length === 0 && directoryResults.length === 0 && <p>{F.empty}</p>}

      {users.length > 0 && (
        <table>
          {/* Not F.heading: the h2 above already reads "Users & roles", and a caption repeating it
              verbatim rendered as a duplicate heading. */}
          <caption>{F.tableCaption}</caption>
          <thead>
            <tr>
              <th scope="col">{F.name}</th>
              <th scope="col">{F.role}</th>
              <th scope="col" />
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.userKey}>
                <td>{user.displayName}</td>
                <td>
                  <select
                    aria-label={`${F.role} for ${user.displayName}`}
                    defaultValue={user.role}
                    onChange={(event) =>
                      setPendingAssignment({ principalId: user.principalId, role: event.target.value, concurrencyToken: user.concurrencyToken })
                    }
                  >
                    {ROLES.map((role) => (
                      <option key={role} value={role}>
                        {roleLabel(role)}
                      </option>
                    ))}
                  </select>
                </td>
                <td>
                  <button
                    type="button"
                    onClick={() =>
                      setPendingAssignment({ principalId: user.principalId, role: user.role, concurrencyToken: user.concurrencyToken })
                    }
                  >
                    {F.assignRole}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {directoryResults.length > 0 && (
        <ul className="eg-admin-users__directory" aria-label={F.aria.directoryResults}>
          {directoryResults
            .filter((person) => !users.some((user) => user.principalId === person.principalId))
            .map((person) => (
              <li key={person.principalId}>
                {person.displayName}
                <button type="button" onClick={() => setPendingAssignment({ principalId: person.principalId, role: PortfolioRoles.Viewer, concurrencyToken: null })}>
                  {F.assignRole}
                </button>
              </li>
            ))}
        </ul>
      )}

      <ConfirmDialog
        open={pendingAssignment !== null}
        title={F.confirmTitle}
        body={F.confirmBody}
        confirmLabel={assigning ? F.assigning : F.confirmConfirm}
        cancelLabel={F.confirmCancel}
        onConfirm={() => void confirmAssign()}
        onCancel={() => setPendingAssignment(null)}
      />
    </section>
  );
}
